import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import _noop from 'lodash/noop';

import { hasVirtualCard } from '../../helpers';
import { getConfig } from '../../redux/actions/index';
import { MODAL_TYPES } from '../../redux/constants';
import { getCardInfo, getConfigSelector } from '../../redux/selectors';
import { ContextValues, ModalType, Params, ProviderProps } from './types';

const Context = React.createContext<ContextValues>({
  modal: null,
  modalState: {},
  config: { refillMessages: [] },
  openReleaseModal: _noop,
  openRefillModal: _noop,
  openNotEnoughBalanceModal: _noop,
  openUpgradeModal: _noop,
  openTransferModal: _noop,
  openDetailsModal: _noop,
  closeModal: _noop,
  isVCActionInProgress: false,
});

export const useVirtualCardFlow = () => {
  return useContext<ContextValues>(Context);
};

export const withVirtualCardFlow = (Component: React.FC) => {
  return (props: any) => {
    const params = useVirtualCardFlow();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Component {...params} {...props} />;
  };
};

export const VirtualCardFlowProvider: FC<ProviderProps> = ({ children, auth }) => {
  const [modal, setModal] = useState<ModalType | null>(null);
  const [modalState, setModalState] = useState<Params>({});

  const dispatch = useDispatch();

  const { data: virtualCardData } = useSelector(getCardInfo);
  const { data: authData = {}, didEnter } = useSelector((state: any) => state.common.auth);
  const { auth: isAuthorized = false, cards = [] } = authData;
  const { data: config } = useSelector(getConfigSelector);

  const wrapFunction = useCallback(
    (
      params: Params,
      additionalParams: Params,
      modalName: ModalType,
      callback: (params?: Params) => void
    ) => {
      setModalState((state) => {
        const newState = {
          ...state,
          [modalName]: params,
          ...additionalParams,
        };

        callback(newState);

        return newState;
      });
    },
    []
  );

  const openAuthModal = useCallback(() => {
    setModal(MODAL_TYPES.AUTH);
    auth.showModal({ preventUserCloseModal: true });
  }, []);

  const openReleaseModal = useCallback(
    (params: Params = {}) => {
      const { withQuickAuthorization } = (params || modalState)?.release || {};

      if (isAuthorized || withQuickAuthorization) {
        setModal(MODAL_TYPES.RELEASE);
      } else {
        openAuthModal();
      }
    },
    [isAuthorized, modalState]
  );

  const openRefillModal = useCallback(() => {
    if (virtualCardData?.phone && hasVirtualCard(cards)) {
      setModal(MODAL_TYPES.REFILL);
    } else {
      openReleaseModal();
    }
  }, [virtualCardData, cards]);

  const closeModal = useCallback(() => {
    setModal(null);
    setModalState({});
  }, [modal, modalState]);

  useEffect(() => {
    // Check auth modal closed
    if (modal === MODAL_TYPES.AUTH && !auth.visible) {
      if (!didEnter) {
        closeModal();

        return;
      }

      if (isAuthorized) {
        // If have virtual card - close release modal
        if (hasVirtualCard(cards)) {
          closeModal();
        } else {
          // If authorized and don't have virtual card - open release modal
          openReleaseModal();
        }
      }
    }
  }, [auth.visible, isAuthorized, cards, modal]);

  useEffect(() => {
    dispatch(getConfig());
  }, [dispatch]);

  return (
    <Context.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        modal,
        modalState,
        config,
        isVCActionInProgress: Boolean(modal),

        openReleaseModal: (params = {}, additionalParams = {}) =>
          wrapFunction(params, additionalParams, MODAL_TYPES.RELEASE, openReleaseModal),

        openRefillModal: (params = {}, additionalParams = {}) =>
          wrapFunction(params, additionalParams, MODAL_TYPES.REFILL, openRefillModal),

        openNotEnoughBalanceModal: (params = {}, additionalParams = {}) =>
          wrapFunction(params, additionalParams, MODAL_TYPES.NOT_ENOUGH_BALANCE, () =>
            setModal(MODAL_TYPES.NOT_ENOUGH_BALANCE)
          ),

        openUpgradeModal: (params = {}, additionalParams = {}) =>
          wrapFunction(params, additionalParams, MODAL_TYPES.UPGRADE, () =>
            setModal(MODAL_TYPES.UPGRADE)
          ),

        openTransferModal: (params = {}, additionalParams = {}) =>
          wrapFunction(params, additionalParams, MODAL_TYPES.TRANSFER, () =>
            setModal(MODAL_TYPES.TRANSFER)
          ),

        openDetailsModal: (params = {}, additionalParams = {}) =>
          wrapFunction(params, additionalParams, MODAL_TYPES.DETAILS, () =>
            setModal(MODAL_TYPES.DETAILS)
          ),

        closeModal,
      }}
    >
      {children}
    </Context.Provider>
  );
};
